import { useRef } from 'react';
import { Check, Pipette } from 'lucide-react';
import {
    CATEGORICAL_KEYS,
    CATEGORICAL_LABELS,
    resolveEntityColor,
    isCategoricalKey,
} from '../../utils/entityColor';
import './ui-components.css';

/**
 * ColorPicker — chooses an entity's colour.
 *
 * WHAT CHANGED AND WHY. The swatches used to be ten literal hexes, so every colour a
 * user picked was frozen at its light-mode value and entity colour was the one part of
 * the UI dark mode never reached. They are now the categorical token keys, which carry a
 * light and a dark value each. See docs/data-schemas.md, "Entity colour".
 *
 * `onChange` emits a KEY ('categorical-3'), not a resolved colour. Storing
 * `var(--categorical-3)` would break consumers that treat the value as a hex, and storing
 * the resolved hex would defeat the point.
 *
 * THE CUSTOM INPUT STAYS. `<input type="color">` can only speak hex, so a custom colour
 * is stored as a hex and stays theme-blind — that is the honest trade, and it is the
 * user's to make. Removing the escape hatch would strand everyone already holding a
 * hand-picked colour, and `resolveEntityColor` renders hexes indefinitely for exactly
 * that reason.
 */
export default function ColorPicker({ value, onChange }) {
    const inputRef = useRef(null);

    const handleCustomChange = (e) => onChange(e.target.value);

    // Custom = anything that is not one of the palette keys, including a legacy hex that
    // the v5 migration did not recognise.
    const isCustom = Boolean(value) && !isCategoricalKey(value);
    const customSwatch = isCustom ? resolveEntityColor(value) : null;

    return (
        <div className="color-picker">
            <div className="color-picker__swatches">
                {CATEGORICAL_KEYS.map(key => {
                    const selected = value === key;
                    return (
                        <button
                            key={key}
                            type="button"
                            className={`color-picker__swatch ${selected ? 'color-picker__swatch--active' : ''}`}
                            style={{
                                background: resolveEntityColor(key),
                                color: 'var(--text-inverse)',
                                width: 'var(--size-lg)',
                                height: 'var(--size-lg)',
                                borderRadius: 'var(--radius-sm)',
                                border: selected
                                    ? 'var(--border-width-default) solid var(--brand-primary)'
                                    : 'var(--border-width-thin) solid transparent',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                padding: 0,
                            }}
                            onClick={() => onChange(key)}
                            title={CATEGORICAL_LABELS[key]}
                            aria-label={CATEGORICAL_LABELS[key]}
                            aria-pressed={selected}
                        >
                            {selected && <Check size={14} style={{ color: 'inherit' }} />}
                        </button>
                    );
                })}

                {/* Custom colour. Hex-only by construction — see the note above. */}
                <button
                    type="button"
                    className={`color-picker__swatch ${isCustom ? 'color-picker__swatch--active' : ''}`}
                    style={{
                        background: isCustom ? customSwatch : 'transparent',
                        color: isCustom ? 'var(--text-inverse)' : 'var(--text-muted)',
                        width: 'var(--size-lg)',
                        height: 'var(--size-lg)',
                        borderRadius: 'var(--radius-sm)',
                        border: isCustom
                            ? 'var(--border-width-default) solid var(--brand-primary)'
                            : 'var(--border-width-thin) dashed var(--text-muted)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        padding: 0,
                        position: 'relative',
                    }}
                    onClick={() => inputRef.current?.click()}
                    title="Custom colour (will not adapt to dark mode)"
                    aria-label="Custom colour"
                >
                    {isCustom ? <Check size={14} /> : <Pipette size={14} />}
                    <input
                        ref={inputRef}
                        type="color"
                        value={isCustom ? value : '#000000'}
                        onChange={handleCustomChange}
                        style={{ position: 'absolute', opacity: 0, width: 0, height: 0, padding: 0, pointerEvents: 'none' }}
                        tabIndex={-1}
                    />
                </button>
            </div>
        </div>
    );
}
